import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { ToastContainer } from "react-toastify";

import HeaderOrg from "../Headers/HeaderOrg";
import ManageTitle from "../../Components/TitleElements/ManageTitle";
import Loader from "../../Components/Loader/Loader";
import Help from "../../Components/Help/Help";
import Button from "../../Components/Button/Button";
import { ShowToastMessage } from "../../helper/ShowToastMessage";
import { serviceConsumer } from "../../network/ServiceConsumer";
import {
  getDrawDashboard,
  getReport,
  downloadReportCsv,
  downloadEventPdf,
} from "../../network/oireachtasEventDay";

// B5 — reporting & reconciliation (tabulation-and-results.md §10, the
// deferred WS-A A7 card): competitor lists (draw order once locked), team
// rosters with override-attestation flags, transactions, assessment/ad
// reconciliation, qualification lists — each with a CSV export — plus the
// print artifacts (competitor-lists PDF, full results book PDF). Read-side
// only; house admin styles per the Organizer/ManageFeisGrade precedent.
const VIEWS = [
  { key: "competitors", label: "Competitor lists" },
  { key: "teams", label: "Team rosters" },
  { key: "transactions", label: "Transactions" },
  { key: "reconciliation", label: "Reconciliation" },
  { key: "qualifiers", label: "Qualifiers (WQ / NAIDC)" },
];

const OireachtasReporting = () => {
  const [event, setEvent] = useState(null);
  const [comps, setComps] = useState([]); // draw dashboard rows (comp picker)
  // The dashboard can deep-link a view (e.g. Transaction History →
  // ?view=transactions); anything unrecognized falls back to the default.
  const [view, setView] = useState(() => {
    const v = new URLSearchParams(window.location.search).get("view");
    return VIEWS.some((x) => x.key === v) ? v : "competitors";
  });
  const [selectedComp, setSelectedComp] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  // Guards against out-of-order responses: switching views while a slow
  // report is in flight must never render one view's payload under another
  // (the payload shapes differ — a stale mismatch is a render crash).
  const requestRef = useRef(0);
  const navigate = useNavigate();
  const location = useLocation();
  const eventId = new URLSearchParams(location.search).get("id");

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        const [eventRes, dash] = await Promise.all([
          serviceConsumer("GET", `${process.env.REACT_APP_BASE_URL}/oireachtas/${eventId}`),
          getDrawDashboard(eventId),
        ]);
        setEvent(eventRes.event);
        setComps(dash.rows || []);
      } catch (e) {
        ShowToastMessage("error", e?.messages || "Could not load reporting");
      } finally {
        setLoading(false);
      }
    })();
  }, [eventId]);

  // Deep-linked non-competitor views (no comp selection needed) load
  // immediately; the default competitors view waits for a selection.
  useEffect(() => {
    if (view !== "competitors") load(view);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const load = async (viewKey, compId) => {
    const requestId = ++requestRef.current;
    if (viewKey === "competitors" && !compId) {
      setData(null);
      return;
    }
    try {
      setBusy(true);
      const params = viewKey === "competitors" ? { comp: compId } : undefined;
      const res = await getReport(eventId, viewKey, params);
      if (requestId !== requestRef.current) return; // stale — a newer load won
      setData(res);
    } catch (e) {
      if (requestId !== requestRef.current) return;
      setData(null);
      ShowToastMessage("error", e?.messages || "Could not load the report");
    } finally {
      if (requestId === requestRef.current) setBusy(false);
    }
  };

  const switchView = (v) => {
    setView(v);
    setData(null);
    // Always goes through load() — its request token invalidates any
    // in-flight response from the previous view (incl. the competitors
    // no-comp early return).
    load(v, v === "competitors" ? selectedComp : undefined);
  };

  const exportCsv = () => {
    const params = view === "competitors" ? { comp: selectedComp } : undefined;
    const compCode = comps.find((r) => String(r.competition._id) === String(selectedComp))
      ?.competition?.code;
    const filename = view === "competitors" ? `competitors-${compCode}.csv` : `${view}.csv`;
    downloadReportCsv(eventId, view, params, filename).catch(() =>
      ShowToastMessage("error", "Export failed")
    );
  };

  const pdf = (path, name) =>
    downloadEventPdf(eventId, path, name).catch(() =>
      ShowToastMessage("error", "The PDF could not be opened (is anything published yet?)")
    );

  const eventDate = (event?.eventDays && event.eventDays[0]) || event?.entriesCloseDate;
  const money = (v) => `$${v}`;

  // Competitor list: round-dance column only when the competition has one.
  const anyRoundDance =
    view === "competitors" && !!data && Array.isArray(data.rows) && data.rows.some((r) => r.roundDance);

  // ---- table renderers (house table conventions) ----
  const table = (headers, rows, renderRow) => (
    <div style={{ width: "100%", overflowX: "auto", marginTop: "20px", marginBottom: "40px" }}>
      <table className="table my_table competitorTable ">
        <tbody>
          <tr className="header-row">
            {headers.map((h) => (
              <td key={h.label} className={`p-25-5 ${h.className || ""}`}>{h.label}</td>
            ))}
          </tr>
          {rows.map(renderRow)}
        </tbody>
      </table>
      {rows.length === 0 && (
        <div className="d-flex justify-content-center my-5">Nothing to report yet</div>
      )}
    </div>
  );
  const td = (v, cls = "") => <td className={`grey-30 p-25-5 ${cls}`}>{v}</td>;
  const rowStyle = { background: "#FFFFFF", fontSize: "12px", height: "31px" };

  return (
    <>
      {loading && <Loader />}
      <HeaderOrg />
      <ManageTitle
        eventLogo={event?.logo}
        eventName={event?.name}
        eventType={event?.eventType}
        eventDate={eventDate}
        loading={loading}
        textButton="Print results book"
        className={loading ? "text-btn-disabled" : "text-btn"}
        textBtnHandler={() =>
          pdf("results-book.pdf", `${event?.name || "event"}-results-book.pdf`)
        }
        onBackClick={() => setTimeout(() => navigate(`/oireachtas/manage?id=${eventId}`), 200)}
      />
      {!loading && (
        <div className="db-body">
          <div className="grey-42 mt-3" style={{ fontSize: "13px" }}>
            Read-side reports over the event&apos;s ground truth — entries, teams, payments,
            assessment roll-ups and tabulated results. Every view exports to CSV; competitor
            lists print as a PDF (draw order once the draw is locked).
          </div>

          <div className="d-md-flex align-items-end mt-4 gap-md-4 flex-wrap">
            <div>
              <div className="search-label">Report:</div>
              <select
                className="check-in-select"
                value={view}
                onChange={(e) => switchView(e.target.value)}
              >
                {VIEWS.map((v) => (
                  <option key={v.key} value={v.key}>{v.label}</option>
                ))}
              </select>
            </div>
            {view === "competitors" && (
              <div>
                <div className="search-label">Competition:</div>
                <select
                  className="check-in-select"
                  value={selectedComp}
                  onChange={(e) => {
                    setSelectedComp(e.target.value);
                    load("competitors", e.target.value);
                  }}
                >
                  <option value="">Select…</option>
                  {comps.map((r) => (
                    <option key={r.competition._id} value={r.competition._id}>
                      {r.competition.code} — {r.entrantCount} entrants
                      {r.draw && r.draw.locked ? "" : " (draw not locked)"}
                    </option>
                  ))}
                </select>
              </div>
            )}
            <div className="d-flex gap-2 mt-3 mt-md-0">
              <Button
                className={
                  busy || !data || (view === "competitors" && !selectedComp)
                    ? "disabledOutlined"
                    : "outlined"
                }
                onClick={exportCsv}
              >
                Export CSV
              </Button>
              {view === "competitors" && (
                <Button
                  className={busy ? "disabledOutlined" : "outlined"}
                  onClick={() =>
                    pdf("reports/competitors.pdf", `${event?.name || "event"}-competitors.pdf`)
                  }
                >
                  All lists (PDF)
                </Button>
              )}
            </div>
          </div>

          {busy && <Loader />}

          {/* ---- competitor list ---- */}
          {!busy && view === "competitors" && data && (
            <>
              <div className="grey-42 mt-3" style={{ fontSize: "13px" }}>
                {data.competition.code} — {data.competition.name}
                {data.hasDraw ? " · draw order" : " · alphabetical (draw not locked)"}
              </div>
              {table(
                [
                  { label: "#", className: "text-center w-5" },
                  { label: "Name", className: "w-30 mx-5" },
                  { label: "School", className: "w-30" },
                  { label: "Set dance", className: "w-25" },
                  { label: "Speed", className: "text-center w-10" },
                  // Only competitions with a round-dance choice send one.
                  ...(anyRoundDance ? [{ label: "Round dance", className: "w-20" }] : []),
                ],
                data.rows,
                (r, i) => (
                  <tr key={i} style={rowStyle}>
                    {td(r.number || r.position, "text-center w-5")}
                    {td(r.name, "w-30 mx-5")}
                    {td(r.school, "w-30 grey-42")}
                    {td(r.setDance, "w-25 grey-42")}
                    {td(r.speed, "text-center w-10")}
                    {anyRoundDance && td(r.roundDance, "w-20 grey-42")}
                  </tr>
                )
              )}
            </>
          )}

          {/* ---- team rosters ---- */}
          {!busy && view === "teams" && data && table(
            [
              { label: "Comp", className: "w-10" },
              { label: "School", className: "w-20" },
              { label: "Dance", className: "w-15" },
              { label: "Members", className: "w-35" },
              { label: "Attested override", className: "w-20" },
            ],
            data.rows,
            (r, i) => (
              <tr key={i} style={rowStyle}>
                {td(r.competition, "w-10")}
                {td(r.school, "w-20")}
                {td(r.dance, "w-15 grey-42")}
                {td(`${r.members} (${r.memberCount})`, "w-35 grey-42")}
                {td(r.attested ? `YES — ${r.overriddenRules}` : "", "w-20")}
              </tr>
            )
          )}

          {/* ---- transactions ---- */}
          {!busy && view === "transactions" && data && (
            <>
              <div className="grey-42 mt-3" style={{ fontSize: "13px" }}>
                Paid: {data.totals.count} payment{data.totals.count === 1 ? "" : "s"} ·
                subtotal {money(data.totals.subtotal)} · service {money(data.totals.serviceFee)} ·
                <b className="grey-30"> total {money(data.totals.total)}</b>
              </div>
              {table(
                [
                  { label: "Receipt", className: "w-10" },
                  { label: "Date", className: "w-10" },
                  { label: "Teacher", className: "w-25" },
                  { label: "Entries", className: "text-center w-10" },
                  { label: "Teams", className: "text-center w-10" },
                  { label: "Total", className: "text-center w-15" },
                  { label: "Status", className: "w-20" },
                ],
                data.rows,
                (r, i) => (
                  <tr key={i} style={rowStyle}>
                    {td(r.receipt, "w-10")}
                    {td(r.date, "w-10 grey-42")}
                    {td(r.teacher, "w-25")}
                    {td(r.entries, "text-center w-10")}
                    {td(r.teams, "text-center w-10")}
                    {td(`${money(r.total)} ${r.currency}`, "text-center w-15")}
                    {td(r.status, "w-20 grey-42")}
                  </tr>
                )
              )}
            </>
          )}

          {/* ---- reconciliation ---- */}
          {!busy && view === "reconciliation" && data && (
            <>
              <div className="grey-42 mt-3" style={{ fontSize: "13px" }}>
                Event totals — pool {money(data.totals.pool)} · allocated{" "}
                {money(data.totals.allocated)} · sponsorship {money(data.totals.sponsorship)} ·
                overage collected {money(data.totals.overageCollected)} ({data.currency})
              </div>
              {table(
                [
                  { label: "School", className: "w-25" },
                  { label: "Pool", className: "text-center w-10" },
                  { label: "Allocated", className: "text-center w-10" },
                  { label: "Sponsorship", className: "text-center w-10" },
                  { label: "Overage collected", className: "text-center w-15" },
                  { label: "Allocations", className: "w-30" },
                ],
                data.rows,
                (r, i) => (
                  <tr key={i} style={rowStyle}>
                    {td(r.school, "w-25")}
                    {td(money(r.pool), "text-center w-10")}
                    {td(money(r.allocated), "text-center w-10")}
                    {td(money(r.sponsorship), "text-center w-10")}
                    {td(money(r.overageCollected), "text-center w-15")}
                    {td(
                      r.items.map((it) => `${it.label} ×${it.qty} (${money(it.amount)})`).join(" · "),
                      "w-30 grey-42"
                    )}
                  </tr>
                )
              )}
            </>
          )}

          {/* ---- qualifiers ---- */}
          {!busy && view === "qualifiers" && data && table(
            [
              { label: "Comp", className: "w-10" },
              { label: "#", className: "text-center w-5" },
              { label: "Dancer", className: "w-25" },
              { label: "School", className: "w-20" },
              { label: "Place", className: "text-center w-10" },
              { label: "Qualification", className: "w-15" },
              { label: "Published", className: "w-15" },
            ],
            data.rows,
            (r, i) => (
              <tr key={i} style={rowStyle}>
                {td(r.competition, "w-10")}
                {td(r.card, "text-center w-5")}
                {td(r.name, "w-25")}
                {td(r.school, "w-20 grey-42")}
                {td(r.position, "text-center w-10")}
                {td(r.qualification, "w-15")}
                {td(r.published, "w-15 grey-42")}
              </tr>
            )
          )}

          {!busy && view === "competitors" && !selectedComp && (
            <div className="d-flex justify-content-center my-5">Select a competition</div>
          )}
        </div>
      )}
      <ToastContainer />
      <Help />
    </>
  );
};

export default OireachtasReporting;
